import { cn } from "@/lib/cn";

type ConnectionStatus = "connected" | "reconnecting" | "offline";

interface ConnectionPipProps {
  status: ConnectionStatus;
  className?: string;
}

const statusConfig: Record<ConnectionStatus, { color: string; label: string; animate: boolean }> = {
  connected: { color: "bg-success", label: "Connected", animate: false },
  reconnecting: { color: "bg-warning", label: "Reconnecting…", animate: true },
  offline: { color: "bg-danger", label: "Offline", animate: true },
};

/**
 * ConnectionPip — topbar live/reconnecting/offline indicator.
 *
 * Shows a small colored dot with optional pulse animation and
 * a tooltip-style label on hover.
 */
export function ConnectionPip({ status, className }: ConnectionPipProps) {
  const config = statusConfig[status];

  return (
    <div className={cn("relative group flex items-center", className)}>
      <div className="relative flex h-2.5 w-2.5">
        {config.animate && (
          <span
            className={cn(
              "absolute inset-0 rounded-full opacity-75 animate-ping",
              config.color,
            )}
          />
        )}
        <span
          className={cn(
            "relative inline-flex h-2.5 w-2.5 rounded-full",
            config.color,
          )}
        />
      </div>

      {/* Hover label */}
      <div
        className={cn(
          "absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1",
          "rounded text-[10px] font-mono whitespace-nowrap",
          "bg-bg-sunken border border-border text-fg-muted shadow-lg",
          "opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150",
        )}
      >
        {config.label}
      </div>
    </div>
  );
}
